import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { useI18n } from '../../context/I18nContext';
import axios from 'axios';
import {
  User,
  Mail,
  Phone,
  MapPin,
  Shield,
  Bell,
  Link as LinkIcon,
  Save,
  CheckCircle,
  AlertCircle,
  Palette,
  Globe,
  CreditCard,
  Receipt,
  FileText,
  HelpCircle,
  MessageCircle,
  Scale,
  Lock,
  ChevronRight,
  ArrowLeft,
  Settings,
  Key,
  Smartphone,
  Eye,
  EyeOff,
  Fingerprint,
  Zap,
  BookOpen,
  ExternalLink,
  PlugZap,
  Sun,
  Moon,
  Monitor,
  Languages,
  Check,
} from 'lucide-react';
import ConnectionsVault from '../../components/settings/ConnectionsVault';
import GoogleAddressInput from '../../components/GoogleAddressInput';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Skeleton } from '../../components/ui/skeleton';
import { Switch } from '../../components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '../../components/ui/dialog';
import { toast } from 'sonner';

// Normalize backend URL to prevent double /api paths
const rawBackendUrl = (process.env.REACT_APP_BACKEND_URL || '').trim().replace(/\/$/, '');
const BACKEND_URL = rawBackendUrl.replace(/\/api$/, '');
const API = `${BACKEND_URL}/api`;

/**
 * FundElite Design System - Theme-aware Colors
 * Uses CSS custom properties that respect dark mode
 */
// These are fallback values only - actual colors come from Tailwind classes
const colors = {
  brandGreen500: '#0FAC6C',
  brandGreen600: '#0D9F64',
  brandGreen700: '#0B8C58',
  brandGreen800: '#08754A',
  mint50: '#F2FCF7',
  mint100: '#E5F8EE',
  mint200: '#D1FAE5',
  appBg: '#F7F9F8',
  pageSurface: '#FFFFFF',
  softSectionBg: '#F3F6F4',
  borderLight: '#E5E9E7',
  borderDefault: '#D9DEDC',
  textPrimary: '#111827',
  textSecondary: '#4B5563',
  textMuted: '#6B7280',
  success: '#10B981',
  warning: '#EAB23A',
  error: '#EF4444',
  info: '#3B82F6',
};

const US_STATES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'
];

// Section Card Component - Theme-aware
const SectionCard = ({ title, description, icon: Icon, children, accentColor = colors.brandGreen500 }) => (
  <div 
    className="bg-white dark:bg-[rgba(10,38,28,0.65)] rounded-xl border border-stone-200 dark:border-[rgba(142,218,182,0.12)] overflow-hidden transition-all duration-200 hover:shadow-md dark:hover:shadow-[0_12px_32px_rgba(0,0,0,0.25)] backdrop-blur-sm"
  >
    <div className="p-5 border-b border-stone-200 dark:border-[rgba(142,218,182,0.10)]">
      <div className="flex items-center gap-3">
        <div 
          className="w-10 h-10 rounded-xl flex items-center justify-center bg-emerald-50 dark:bg-[rgba(16,185,129,0.12)]"
        >
          <Icon size={20} className="text-emerald-600 dark:text-emerald-400" />
        </div>
        <div>
          <h3 className="font-semibold text-stone-900 dark:text-[#EAF7F0]">{title}</h3>
          {description && (
            <p className="text-sm mt-0.5 text-stone-500 dark:text-[#93AE9F]">{description}</p>
          )}
        </div>
      </div>
    </div>
    <div className="p-5">
      {children}
    </div>
  </div>
);

// Settings Item Row - Theme-aware
const SettingsItem = ({ 
  icon: Icon, 
  label, 
  description, 
  action, 
  onClick, 
  to, 
  badge, 
  disabled = false,
  external = false 
}) => {
  const content = (
    <div 
      className={`flex items-center justify-between p-3 rounded-xl transition-all duration-150 ${
        disabled 
          ? 'opacity-50 cursor-not-allowed' 
          : 'hover:bg-stone-50 dark:hover:bg-[rgba(142,218,182,0.08)] cursor-pointer'
      }`}
      onClick={disabled ? undefined : onClick}
    >
      <div className="flex items-center gap-3">
        {Icon && (
          <div className="w-9 h-9 rounded-lg flex items-center justify-center bg-stone-100 dark:bg-[rgba(142,218,182,0.10)]">
            <Icon size={18} className="text-stone-500 dark:text-[#93AE9F]" />
          </div>
        )}
        <div>
          <p className="font-medium text-sm text-stone-900 dark:text-[#EAF7F0]">{label}</p>
          {description && (
            <p className="text-xs text-stone-500 dark:text-[#93AE9F]">{description}</p>
          )}
        </div>
      </div>
      <div className="flex items-center gap-2">
        {badge && (
          <span 
            className={`px-2 py-0.5 rounded-full text-xs font-medium ${
              badge.type === 'success' 
                ? 'bg-emerald-100 text-emerald-700 dark:bg-[rgba(16,185,129,0.15)] dark:text-emerald-400' 
                : badge.type === 'warning' 
                ? 'bg-amber-100 text-amber-700 dark:bg-[rgba(245,158,11,0.15)] dark:text-amber-400'
                : 'bg-stone-100 text-stone-600 dark:bg-[rgba(142,218,182,0.10)] dark:text-[#93AE9F]'
            }`}
          >
            {badge.text}
          </span>
        )}
        {action || (
          external 
            ? <ExternalLink size={16} className="text-stone-400 dark:text-[#6B8878]" /> 
            : <ChevronRight size={16} className="text-stone-400 dark:text-[#6B8878]" />
        )}
      </div>
    </div>
  );

  if (to && !disabled) {
    if (external) {
      return (
        <a href={to} target="_blank" rel="noopener noreferrer">
          {content}
        </a>
      );
    }
    return <Link to={to}>{content}</Link>;
  }
  return content;
};

// Main Settings Hub Component
const SettingsHub = ({ initialSection = null }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, getAuthHeaders, isSupportMode = false } = useAuth();
  const { t, language, setLanguage, theme, setTheme, availableLanguages, availableThemes } = useI18n();
  const [activeSection, setActiveSection] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  
  // Check for section from URL params or initialSection prop
  useEffect(() => {
    // First priority: initialSection prop (for direct routes like /profile)
    if (initialSection) {
      setActiveSection(initialSection);
      return;
    }
    
    // Second priority: URL query param (?section=profile)
    const params = new URLSearchParams(location.search);
    const sectionParam = params.get('section');
    if (sectionParam && ['profile', 'security', 'notifications', 'connections'].includes(sectionParam)) {
      setActiveSection(sectionParam);
    }
  }, [initialSection, location.search]);
  
  // Theme and Language modal states
  const [themeModalOpen, setThemeModalOpen] = useState(false);
  const [languageModalOpen, setLanguageModalOpen] = useState(false);
  
  const [profileData, setProfileData] = useState({
    name: '',
    email: '',
    phone: '',
    address_line1: '',
    address_line2: '',
    city: '',
    state: '',
    postal_code: '',
    // Protected identity field
    has_last4_ssn: false,
    last4_ssn_masked: null
  });
  
  // State for protected Last 4 SSN field
  const [last4SsnEditMode, setLast4SsnEditMode] = useState(false);
  const [last4SsnValue, setLast4SsnValue] = useState('');
  const [last4SsnError, setLast4SsnError] = useState('');
  const [savingLast4Ssn, setSavingLast4Ssn] = useState(false);
  
  // Reveal state for Last 4 SSN
  const [last4SsnRevealed, setLast4SsnRevealed] = useState(false);
  const [last4SsnRevealedValue, setLast4SsnRevealedValue] = useState('');
  const [revealingLast4Ssn, setRevealingLast4Ssn] = useState(false);
  const [revealCountdown, setRevealCountdown] = useState(0);
  
  const [notifications, setNotifications] = useState({
    dispute_updates: true,
    score_changes: true,
    marketing: false,
    sms: false
  });

  useEffect(() => {
    fetchProfile();
  }, []);

  const fetchProfile = async () => {
    try {
      const response = await axios.get(`${API}/users/profile`, {
        headers: getAuthHeaders(),
        withCredentials: true
      });
      
      const profile = response.data;
      setProfileData({
        name: profile.name || '',
        email: profile.email || '',
        phone: profile.phone || '',
        address_line1: profile.mailing_address?.line1 || '',
        address_line2: profile.mailing_address?.line2 || '',
        city: profile.mailing_address?.city || '',
        state: profile.mailing_address?.state || '',
        postal_code: profile.mailing_address?.postal_code || '',
        // Protected identity field
        has_last4_ssn: profile.has_last4_ssn || false,
        last4_ssn_masked: profile.last4_ssn_masked || null
      });
    } catch (error) {
      console.error('Profile fetch error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveProfile = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/users/profile`, profileData, {
        headers: getAuthHeaders(),
        withCredentials: true
      });
      toast.success('Profile saved successfully');
    } catch (error) {
      console.error('Save error:', error);
      toast.error('Failed to save profile');
    } finally {
      setSaving(false);
    }
  };

  const handleChange = (field, value) => {
    setProfileData(prev => ({ ...prev, [field]: value }));
  };

  // Handler for Last 4 SSN validation and save
  const handleLast4SsnChange = (value) => {
    // Only allow numeric input
    const numericValue = value.replace(/\D/g, '').slice(0, 4);
    setLast4SsnValue(numericValue);
    
    // Clear error when user is typing
    if (last4SsnError) {
      setLast4SsnError('');
    }
  };

  const validateLast4Ssn = (value) => {
    if (!value || value.length === 0) {
      return ''; // Allow empty/clearing
    }
    if (value.length !== 4) {
      return 'Enter exactly 4 digits';
    }
    if (!/^\d{4}$/.test(value)) {
      return 'Enter exactly 4 digits';
    }
    return '';
  };

  const handleSaveLast4Ssn = async () => {
    const error = validateLast4Ssn(last4SsnValue);
    if (error) {
      setLast4SsnError(error);
      return;
    }

    setSavingLast4Ssn(true);
    try {
      await axios.put(`${API}/users/profile`, {
        last4_ssn: last4SsnValue || null
      }, {
        headers: getAuthHeaders(),
        withCredentials: true
      });
      
      // Update local state
      setProfileData(prev => ({
        ...prev,
        has_last4_ssn: !!last4SsnValue,
        last4_ssn_masked: last4SsnValue ? '••••' : null
      }));
      
      setLast4SsnEditMode(false);
      setLast4SsnValue('');
      toast.success('Identity information updated');
    } catch (error) {
      console.error('Save last4_ssn error:', error);
      const message = error.response?.data?.detail || 'Failed to save';
      setLast4SsnError(message);
      toast.error(message);
    } finally {
      setSavingLast4Ssn(false);
    }
  };

  const handleCancelLast4SsnEdit = () => {
    setLast4SsnEditMode(false);
    setLast4SsnValue('');
    setLast4SsnError('');
  };

  // Reveal Last 4 SSN securely
  const handleRevealLast4Ssn = async () => {
    // Block reveal in support/impersonation mode
    if (isSupportMode) {
      toast.error('Cannot reveal protected data in support mode');
      return;
    }

    setRevealingLast4Ssn(true);
    try {
      const response = await axios.get(`${API}/users/profile/reveal-ssn`, {
        headers: getAuthHeaders(),
        withCredentials: true
      });
      
      const { last4_ssn, auto_hide_seconds } = response.data;
      setLast4SsnRevealedValue(last4_ssn);
      setLast4SsnRevealed(true);
      setRevealCountdown(auto_hide_seconds || 8);
      
      // Start countdown timer
      const countdownInterval = setInterval(() => {
        setRevealCountdown(prev => {
          if (prev <= 1) {
            clearInterval(countdownInterval);
            handleHideLast4Ssn();
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
      
    } catch (error) {
      console.error('Reveal last4_ssn error:', error);
      const message = error.response?.data?.detail || 'Unable to reveal';
      toast.error(message);
    } finally {
      setRevealingLast4Ssn(false);
    }
  };

  const handleHideLast4Ssn = () => {
    setLast4SsnRevealed(false);
    setLast4SsnRevealedValue('');
    setRevealCountdown(0);
  };

  // Calculate profile completion
  const profileCompletion = () => {
    const fields = ['name', 'email', 'phone', 'address_line1', 'city', 'state', 'postal_code'];
    const filled = fields.filter(f => profileData[f]?.trim()).length;
    return Math.round((filled / fields.length) * 100);
  };

  if (loading) {
    return (
      <div className="space-y-6" data-testid="settings-loading">
        <Skeleton className="h-12 w-64" />
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <Skeleton className="h-64" />
          <Skeleton className="h-64" />
        </div>
      </div>
    );
  }

  // If a section is active, show the detail view
  if (activeSection) {
    return (
      <div className="space-y-6" data-testid={`settings-${activeSection}`}>
        {/* Back navigation */}
        <button 
          onClick={() => {
            setActiveSection(null);
            // Navigate to /settings to clear any section-specific URL
            navigate('/settings');
          }}
          className="flex items-center gap-2 text-sm font-medium transition-colors hover:opacity-80 text-emerald-600 dark:text-emerald-400"
        >
          <ArrowLeft size={16} />
          Back to Settings
        </button>

        {/* Profile Section */}
        {activeSection === 'profile' && (
          <SectionCard title="Profile Information" icon={User} description="Your personal details and mailing address">
            <div className="space-y-5">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="name" className="text-stone-700 dark:text-[#C7DDD0]">Full Name</Label>
                  <Input
                    id="name"
                    value={profileData.name}
                    onChange={(e) => handleChange('name', e.target.value)}
                    placeholder="John Doe"
                    className="dark:bg-[rgba(10,38,28,0.65)] dark:border-[rgba(142,218,182,0.12)] dark:text-[#EAF7F0] dark:placeholder-[#6B8878]"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-stone-700 dark:text-[#C7DDD0]">Email Address</Label>
                  <Input
                    id="email"
                    type="email"
                    value={profileData.email}
                    disabled
                    className="bg-gray-50 dark:bg-[rgba(8,32,24,0.65)] dark:border-[rgba(142,218,182,0.08)] dark:text-[#93AE9F]"
                  />
                  <p className="text-xs text-stone-500 dark:text-[#93AE9F]">Email cannot be changed</p>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="phone" className="text-stone-700 dark:text-[#C7DDD0]">Phone Number</Label>
                <Input
                  id="phone"
                  value={profileData.phone}
                  onChange={(e) => handleChange('phone', e.target.value)}
                  placeholder="(555) 555-5555"
                  className="dark:bg-[rgba(10,38,28,0.65)] dark:border-[rgba(142,218,182,0.12)] dark:text-[#EAF7F0] dark:placeholder-[#6B8878]"
                />
              </div>

              {/* Protected Last 4 of SSN Field */}
              <div className="space-y-2">
                <Label htmlFor="last4_ssn" className="flex items-center gap-2 text-stone-700 dark:text-[#C7DDD0]">
                  <Lock size={14} className="text-stone-400 dark:text-[#6B8878]" />
                  Last 4 of SSN
                </Label>
                
                {/* Display Mode vs Edit Mode */}
                {!last4SsnEditMode ? (
                  <div className="flex items-center gap-3">
                    <div className="flex-1 h-10 px-3 rounded-md flex items-center justify-between bg-stone-100 dark:bg-[rgba(8,32,24,0.65)] border border-stone-200 dark:border-[rgba(142,218,182,0.12)]">
                      {profileData.has_last4_ssn ? (
                        <span className="font-mono tracking-wider text-stone-600 dark:text-[#C7DDD0]">
                          {last4SsnRevealed ? last4SsnRevealedValue : (profileData.last4_ssn_masked || '••••')}
                        </span>
                      ) : (
                        <span className="text-stone-400 dark:text-[#6B8878]">Not set</span>
                      )}
                      <div className="flex items-center gap-2">
                        {profileData.has_last4_ssn && (
                          <span className="text-xs px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-700 dark:bg-[rgba(16,185,129,0.15)] dark:text-emerald-400">
                            Protected
                          </span>
                        )}
                        {last4SsnRevealed && revealCountdown > 0 && (
                          <span className="text-xs px-2 py-0.5 rounded-full bg-stone-100 text-stone-500 dark:bg-[rgba(142,218,182,0.10)] dark:text-[#93AE9F]">
                            {revealCountdown}s
                          </span>
                        )}
                      </div>
                    </div>
                    
                    {/* Action buttons - conditionally show based on state and permissions */}
                    <div className="flex items-center gap-2">
                      {/* Reveal/Hide button - only show for actual user, not support mode */}
                      {profileData.has_last4_ssn && !isSupportMode && (
                        <>
                          {last4SsnRevealed ? (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={handleHideLast4Ssn}
                              className="shrink-0"
                            >
                              <EyeOff size={14} className="mr-1" />
                              Hide
                            </Button>
                          ) : (
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={handleRevealLast4Ssn}
                              disabled={revealingLast4Ssn}
                              className="shrink-0"
                            >
                              <Eye size={14} className="mr-1" />
                              {revealingLast4Ssn ? '...' : 'Reveal'}
                            </Button>
                          )}
                        </>
                      )}
                      
                      {/* Edit button - hide in support mode */}
                      {!isSupportMode && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => {
                            handleHideLast4Ssn(); // Hide revealed value when entering edit mode
                            setLast4SsnEditMode(true);
                          }}
                          className="shrink-0"
                        >
                          {profileData.has_last4_ssn ? 'Edit' : 'Add'}
                        </Button>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="space-y-2">
                    <div className="flex items-center gap-2">
                      <Input
                        id="last4_ssn"
                        type="password"
                        inputMode="numeric"
                        pattern="[0-9]*"
                        maxLength={4}
                        value={last4SsnValue}
                        onChange={(e) => handleLast4SsnChange(e.target.value)}
                        placeholder="1234"
                        className={`font-mono tracking-wider dark:bg-[rgba(10,38,28,0.65)] dark:border-[rgba(142,218,182,0.12)] dark:text-[#EAF7F0] dark:placeholder-[#6B8878] ${last4SsnError ? 'border-red-500 focus:ring-red-500' : ''}`}
                        autoComplete="off"
                        data-testid="last4-ssn-input"
                      />
                      <Button
                        size="sm"
                        onClick={handleSaveLast4Ssn}
                        disabled={savingLast4Ssn}
                        className="bg-emerald-600 hover:bg-emerald-700 text-white shrink-0"
                      >
                        {savingLast4Ssn ? 'Saving...' : 'Save'}
                      </Button>
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={handleCancelLast4SsnEdit}
                        className="shrink-0"
                      >
                        Cancel
                      </Button>
                    </div>
                    {last4SsnError && (
                      <p className="text-xs flex items-center gap-1 text-red-500 dark:text-red-400">
                        <AlertCircle size={12} />
                        {last4SsnError}
                      </p>
                    )}
                  </div>
                )}
                
                <p className="text-xs text-stone-500 dark:text-[#93AE9F]">
                  Used for identity verification and dispute-related workflows when required.
                </p>
              </div>

              <div className="pt-2 border-t border-stone-200 dark:border-[rgba(142,218,182,0.10)]">
                <h4 className="font-medium mb-3 text-stone-900 dark:text-[#EAF7F0]">Mailing Address</h4>
                <p className="text-xs mb-4 text-stone-500 dark:text-[#93AE9F]">
                  Used as your return address for dispute letters. 
                  This address becomes your protected identity anchor.
                </p>
                
                {/* Google-powered Address Input with verification */}
                <GoogleAddressInput
                  label=""
                  placeholder="Street Address"
                  sourceScreen="settings_profile"
                  initialValue={profileData.address_line1 || ''}
                  initialStreetLine2={profileData.address_line2 || ''}
                  initialCity={profileData.city || ''}
                  initialState={profileData.state || ''}
                  initialZip={profileData.postal_code || ''}
                  onAddressComplete={(normalizedAddress) => {
                    // Update profile data with complete address
                    handleChange('address_line1', normalizedAddress.street_address || '');
                    handleChange('address_line2', normalizedAddress.unit || '');
                    handleChange('city', normalizedAddress.city || '');
                    handleChange('state', normalizedAddress.state || '');
                    handleChange('postal_code', normalizedAddress.zip || '');
                  }}
                  onChange={(data) => {
                    // Update fields as user types
                    if (data.street_address !== undefined) {
                      handleChange('address_line1', data.street_address || '');
                    }
                    if (data.unit !== undefined) {
                      handleChange('address_line2', data.unit || '');
                    }
                    if (data.city !== undefined) {
                      handleChange('city', data.city || '');
                    }
                    if (data.state !== undefined) {
                      handleChange('state', data.state || '');
                    }
                    if (data.zip !== undefined) {
                      handleChange('postal_code', data.zip || '');
                    }
                  }}
                  showSaveButton={false}
                />
              </div>

              <div className="flex justify-end pt-4">
                <Button 
                  onClick={handleSaveProfile} 
                  disabled={saving}
                  className="text-white"
                  style={{ background: colors.brandGreen500 }}
                >
                  {saving ? 'Saving...' : (
                    <>
                      <Save size={16} className="mr-2" />
                      Save Changes
                    </>
                  )}
                </Button>
              </div>
            </div>
          </SectionCard>
        )}

        {/* Security Section */}
        {activeSection === 'security' && (
          <SectionCard title="Security" icon={Shield} description="Protect your account">
            <div className="space-y-3">
              <SettingsItem 
                icon={Key}
                label="Change Password"
                description="Update your password regularly"
                badge={{ text: 'Coming Soon', type: 'default' }}
                disabled
              />
              <SettingsItem 
                icon={Fingerprint}
                label="Two-Factor Authentication"
                description="Add extra security with 2FA"
                badge={{ text: 'Coming Soon', type: 'default' }}
                disabled
              />
              <SettingsItem 
                icon={Smartphone}
                label="Login Sessions"
                description="View and manage active sessions"
                badge={{ text: 'Coming Soon', type: 'default' }}
                disabled
              />
            </div>
          </SectionCard>
        )}

        {/* Notifications Section */}
        {activeSection === 'notifications' && (
          <SectionCard title="Notifications" icon={Bell} description="Manage your notification preferences">
            <div className="space-y-4">
              {[
                { key: 'dispute_updates', label: 'Dispute Updates', description: 'Get notified about dispute progress' },
                { key: 'score_changes', label: 'Score Changes', description: 'Alerts when your credit score changes' },
                { key: 'marketing', label: 'Marketing & Tips', description: 'Credit repair tips and promotions' },
                { key: 'sms', label: 'SMS Notifications', description: 'Receive text message alerts' },
              ].map((item) => (
                <div 
                  key={item.key}
                  className="flex items-center justify-between p-3 rounded-xl bg-stone-50 dark:bg-[rgba(10,38,28,0.50)]"
                >
                  <div>
                    <p className="font-medium text-sm text-stone-900 dark:text-[#EAF7F0]">{item.label}</p>
                    <p className="text-xs text-stone-500 dark:text-[#93AE9F]">{item.description}</p>
                  </div>
                  <Switch 
                    checked={notifications[item.key]}
                    onCheckedChange={(checked) => setNotifications(prev => ({ ...prev, [item.key]: checked }))}
                  />
                </div>
              ))}
              <p className="text-xs pt-2 text-stone-500 dark:text-[#93AE9F]">
                Notification preferences will be saved automatically when feature is fully enabled.
              </p>
            </div>
          </SectionCard>
        )}

        {/* Connections Section */}
        {activeSection === 'connections' && (
          <SectionCard title="Connections" icon={PlugZap} description="Secure login vault for report imports and bureau access">
            <ConnectionsVault />
          </SectionCard>
        )}
      </div>
    );
  }

  // Main Settings Hub View
  return (
    <div className="space-y-6" data-testid="settings-hub">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-stone-900 dark:text-[#EAF7F0]">{t('settings.title')}</h1>
        <p className="text-sm mt-1 text-stone-500 dark:text-[#93AE9F]">
          {t('settings.subtitle')}
        </p>
      </div>

      {/* Settings Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        
        {/* ========== ACCOUNT SECTION ========== */}
        <div className="space-y-4">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-stone-500 dark:text-[#93AE9F]">
            {t('settings.sections.account')}
          </h2>
          
          <div className="bg-white dark:bg-[rgba(10,38,28,0.65)] rounded-xl border border-stone-200 dark:border-[rgba(142,218,182,0.12)] overflow-hidden backdrop-blur-sm">
            <SettingsItem 
              icon={User}
              label={t('settings.profile.title')}
              description={t('settings.profile.description')}
              onClick={() => setActiveSection('profile')}
              badge={{ text: `${profileCompletion()}%`, type: profileCompletion() === 100 ? 'success' : 'warning' }}
            />
            <div className="border-t border-stone-200 dark:border-[rgba(142,218,182,0.08)]" />
            <SettingsItem 
              icon={Shield}
              label={t('settings.security.title')}
              description={t('settings.security.description')}
              onClick={() => setActiveSection('security')}
            />
            <div className="border-t border-stone-200 dark:border-[rgba(142,218,182,0.08)]" />
            <SettingsItem 
              icon={Bell}
              label={t('settings.notifications.title')}
              description={t('settings.notifications.description')}
              onClick={() => setActiveSection('notifications')}
            />
            <div className="border-t border-stone-200 dark:border-[rgba(142,218,182,0.08)]" />
            <SettingsItem 
              icon={PlugZap}
              label={t('settings.connections.title')}
              description={t('settings.connections.description')}
              onClick={() => setActiveSection('connections')}
            />
          </div>
        </div>

        {/* ========== PREFERENCES SECTION ========== */}
        <div className="space-y-4">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-stone-500 dark:text-[#93AE9F]">
            {t('settings.sections.preferences')}
          </h2>
          
          <div className="bg-white dark:bg-[rgba(10,38,28,0.65)] rounded-xl border border-stone-200 dark:border-[rgba(142,218,182,0.12)] overflow-hidden backdrop-blur-sm">
            <SettingsItem 
              icon={Palette}
              label={t('settings.theme.title')}
              description={t('settings.theme.description')}
              badge={{ 
                text: theme === 'light' ? t('settings.theme.light') 
                    : theme === 'dark' ? t('settings.theme.dark') 
                    : t('settings.theme.system'), 
                type: 'default' 
              }}
              onClick={() => setThemeModalOpen(true)}
            />
            <div className="border-t border-stone-200 dark:border-[rgba(142,218,182,0.08)]" />
            <SettingsItem 
              icon={Globe}
              label={t('settings.language.title')}
              description={t('settings.language.description')}
              badge={{ 
                text: language === 'en' ? t('settings.language.english') : t('settings.language.spanish'), 
                type: 'default' 
              }}
              onClick={() => setLanguageModalOpen(true)}
            />
          </div>
        </div>
        
        {/* Theme Selection Modal */}
        <Dialog open={themeModalOpen} onOpenChange={setThemeModalOpen}>
          <DialogContent className="sm:max-w-sm">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Palette size={20} />
                {t('settings.theme.selectTheme')}
              </DialogTitle>
              <DialogDescription>
                {t('settings.theme.description')}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-2 py-4">
              {[
                { value: 'light', label: t('settings.theme.light'), icon: Sun },
                { value: 'dark', label: t('settings.theme.dark'), icon: Moon },
                { value: 'system', label: t('settings.theme.system'), icon: Monitor },
              ].map(({ value, label, icon: Icon }) => (
                <button
                  key={value}
                  onClick={() => {
                    setTheme(value);
                    setThemeModalOpen(false);
                    toast.success(t('settings.theme.themeUpdated'));
                  }}
                  className={`w-full flex items-center justify-between p-3 rounded-lg border transition-all ${
                    theme === value 
                      ? 'border-emerald-500 bg-emerald-50 dark:bg-[rgba(16,185,129,0.15)]' 
                      : 'border-stone-200 dark:border-[rgba(142,218,182,0.12)] hover:border-stone-300 dark:hover:border-[rgba(142,218,182,0.18)] hover:bg-stone-50 dark:hover:bg-[rgba(142,218,182,0.08)]'
                  }`}
                  data-testid={`theme-option-${value}`}
                >
                  <div className="flex items-center gap-3">
                    <Icon size={20} className={theme === value ? 'text-emerald-600 dark:text-emerald-400' : 'text-stone-500 dark:text-[#93AE9F]'} />
                    <span className={theme === value ? 'font-medium text-emerald-700 dark:text-emerald-400' : 'text-stone-700 dark:text-[#EAF7F0]'}>
                      {label}
                    </span>
                  </div>
                  {theme === value && <Check size={18} className="text-emerald-600 dark:text-emerald-400" />}
                </button>
              ))}
            </div>
          </DialogContent>
        </Dialog>
        
        {/* Language Selection Modal */}
        <Dialog open={languageModalOpen} onOpenChange={setLanguageModalOpen}>
          <DialogContent className="sm:max-w-sm">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Languages size={20} />
                {t('settings.language.selectLanguage')}
              </DialogTitle>
              <DialogDescription>
                {t('settings.language.description')}
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-2 py-4">
              {[
                { value: 'en', label: 'English', nativeLabel: 'English' },
                { value: 'es', label: 'Spanish', nativeLabel: 'Español' },
              ].map(({ value, label, nativeLabel }) => (
                <button
                  key={value}
                  onClick={() => {
                    setLanguage(value);
                    setLanguageModalOpen(false);
                    toast.success(value === 'en' ? 'Language updated' : 'Idioma actualizado');
                  }}
                  className={`w-full flex items-center justify-between p-3 rounded-lg border transition-all ${
                    language === value 
                      ? 'border-emerald-500 bg-emerald-50 dark:bg-[rgba(16,185,129,0.15)]' 
                      : 'border-stone-200 dark:border-[rgba(142,218,182,0.12)] hover:border-stone-300 dark:hover:border-[rgba(142,218,182,0.18)] hover:bg-stone-50 dark:hover:bg-[rgba(142,218,182,0.08)]'
                  }`}
                  data-testid={`language-option-${value}`}
                >
                  <div className="flex flex-col items-start">
                    <span className={language === value ? 'font-medium text-emerald-700 dark:text-emerald-400' : 'text-stone-700 dark:text-[#EAF7F0]'}>
                      {nativeLabel}
                    </span>
                    <span className="text-xs text-stone-500 dark:text-[#93AE9F]">{label}</span>
                  </div>
                  {language === value && <Check size={18} className="text-emerald-600 dark:text-emerald-400" />}
                </button>
              ))}
            </div>
          </DialogContent>
        </Dialog>

        {/* ========== MEMBERSHIP SECTION ========== */}
        <div className="space-y-4">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-stone-500 dark:text-[#93AE9F]">
            {t('settings.sections.membership')}
          </h2>
          
          <div className="bg-white dark:bg-[rgba(10,38,28,0.65)] rounded-xl border border-stone-200 dark:border-[rgba(142,218,182,0.12)] overflow-hidden backdrop-blur-sm">
            <SettingsItem 
              icon={CreditCard}
              label={t('settings.subscription.title')}
              description={t('settings.subscription.description')}
              badge={{ text: 'Auto Starter', type: 'success' }}
              disabled
            />
            <div className="border-t border-stone-200 dark:border-[rgba(142,218,182,0.08)]" />
            <SettingsItem 
              icon={Zap}
              label={t('settings.credits.title')}
              description={t('settings.credits.description')}
              badge={{ text: `10 ${t('settings.credits.available')}`, type: 'default' }}
              disabled
            />
            <div className="border-t border-stone-200 dark:border-[rgba(142,218,182,0.08)]" />
            <SettingsItem 
              icon={Receipt}
              label={t('settings.invoices.title')}
              description={t('settings.invoices.description')}
              disabled
            />
          </div>
        </div>

        {/* ========== RESOURCES SECTION ========== */}
        <div className="space-y-4">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-stone-500 dark:text-[#93AE9F]">
            {t('settings.sections.resources')}
          </h2>
          
          <div className="bg-white dark:bg-[rgba(10,38,28,0.65)] rounded-xl border border-stone-200 dark:border-[rgba(142,218,182,0.12)] overflow-hidden backdrop-blur-sm">
            <SettingsItem 
              icon={HelpCircle}
              label={t('settings.helpCenter.title')}
              description={t('settings.helpCenter.description')}
              to="https://fundelite.ai/help"
              external
            />
            <div className="border-t border-stone-200 dark:border-[rgba(142,218,182,0.08)]" />
            <SettingsItem 
              icon={MessageCircle}
              label={t('settings.contactUs.title')}
              description={t('settings.contactUs.description')}
              to="mailto:support@fundelite.ai"
              external
            />
            <div className="border-t border-stone-200 dark:border-[rgba(142,218,182,0.08)]" />
            <SettingsItem 
              icon={Scale}
              label={t('settings.fcraRights.title')}
              description={t('settings.fcraRights.description')}
              to="/academy/fcra-rights"
            />
          </div>
        </div>

        {/* ========== LEGAL SECTION ========== */}
        <div className="space-y-4 lg:col-span-2">
          <h2 className="text-sm font-semibold uppercase tracking-wider text-stone-500 dark:text-[#93AE9F]">
            {t('settings.sections.legal')}
          </h2>
          
          <div className="bg-white dark:bg-[rgba(10,38,28,0.65)] rounded-xl border border-stone-200 dark:border-[rgba(142,218,182,0.12)] overflow-hidden backdrop-blur-sm">
            <div className="grid grid-cols-1 md:grid-cols-2">
              <SettingsItem 
                icon={FileText}
                label={t('settings.termsOfUse.title')}
                description={t('settings.termsOfUse.description')}
                to="https://fundelite.ai/terms"
                external
              />
              <div className="border-t md:border-t-0 md:border-l border-stone-200 dark:border-[rgba(142,218,182,0.08)]">
                <SettingsItem 
                  icon={Lock}
                  label={t('settings.privacyPolicy.title')}
                  description={t('settings.privacyPolicy.description')}
                  to="https://fundelite.ai/privacy"
                  external
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Profile Completion Card */}
      {profileCompletion() < 100 && (
        <div 
          className="rounded-xl p-5 border flex items-center justify-between bg-emerald-50 dark:bg-[rgba(16,185,129,0.12)] border-emerald-200 dark:border-[rgba(52,211,153,0.18)]"
        >
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 rounded-xl flex items-center justify-center bg-emerald-100 dark:bg-[rgba(16,185,129,0.18)]">
              <User size={24} className="text-emerald-600 dark:text-emerald-400" />
            </div>
            <div>
              <p className="font-semibold text-stone-900 dark:text-[#EAF7F0]">
                {t('settings.profile.completeYourProfile')}
              </p>
              <p className="text-sm text-stone-600 dark:text-[#C7DDD0]">
                {100 - profileCompletion()}% {t('settings.profile.remaining')} • {t('settings.profile.helpsWithDisputeAutomation')}
              </p>
            </div>
          </div>
          <Button 
            onClick={() => setActiveSection('profile')}
            className="bg-emerald-600 hover:bg-emerald-700 text-white"
          >
            {t('settings.profile.completeProfile')}
          </Button>
        </div>
      )}
    </div>
  );
};

export default SettingsHub;
